import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';
import { useRevolut, useTranslation } from '@eti/providers';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { useFormContext } from 'react-hook-form';
import isFunction from 'lodash/isFunction';
import { breakpoints, Inline, Stack, vars } from '@etg/wings';
import { ReactComponent as AirplaneDepartureIcon } from '@eti/assets/icons/revolut/airplane-take-off.svg';
import { ReactComponent as AirplaneArrivalIcon } from '@eti/assets/icons/revolut/airplane-landing.svg';
import { ReactComponent as SwitchDirection } from '@eti/assets/icons/revolut/switch-arrows.svg';
import { useMediaQuery } from 'usehooks-ts';
import { AirplaneLanding, AirplaneTakeoff } from '@phosphor-icons/react';
import LocationCombobox from './locationCombobox/LocationCombobox';
import SwitchDirectionButton from './SwitchDirectionButton';
import ZeroWidthSpace from './ZeroWidthSpace';

const switchDirectionButtonStyles = css`
  height: 48px;
  width: 48px;
`;

const locationWrapperStyles = css`
  flex-grow: 1;
  position: relative;
`;

const switchDirectionStyles = css`
  align-items: center;
  background-color: light-dark(#fff, #000);
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 28px;
  transform: translate(-50%, -50%);
  width: 32px;
  z-index: 2;
`;

const switchArrowsStyles = css`
  fill: light-dark(#282828, #fff);
  height: 13px;
  width: 12px;
`;

const iconStyles = css`
  fill: ${vars.colors.text.secondary};
`;

const rtlMirrored = css`
  [dir='rtl'] & {
    transform: scaleX(-1);
  }
`;

const ariaLiveStyles = css`
  position: absolute;
`;

type OriginAndDestinationProps = {
  boundIndex?: number;
  destinationName: string;
  originName: string;
  setAreOriginAndDestinationFilled?: Dispatch<SetStateAction<boolean>>;
};

const OriginAndDestination = ({
  boundIndex,
  destinationName,
  originName,
  setAreOriginAndDestinationFilled,
}: OriginAndDestinationProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [switchLocationsLabel, setSwitchLocationsLabel] = useState<string | undefined>(undefined);
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);
  const { isRevolut } = useRevolut();

  const onSwitchDirection = () => {
    const [origin, destination] = getValues([originName, destinationName]);

    setValue(destinationName, origin);
    setValue(originName, destination);
    setSwitchLocationsLabel(
      `${t('Searchform.Origin.Label')} ${destination?.name}, ${t(
        'Searchform.Destination.Label',
      )} ${origin?.name}`,
    );
  };

  const handleDestinationOnBlur = () => {
    const [origin, destination] = getValues([originName, destinationName]);

    if (destination !== null && origin !== null && isFunction(setAreOriginAndDestinationFilled)) {
      setAreOriginAndDestinationFilled(true);
    }
  };

  const getButtonDataTestId = () =>
    boundIndex && boundIndex >= 0
      ? `searchForm-multiBound[${boundIndex}]-exchangeLocations-button`
      : 'searchForm-singleBound-exchangeLocations-button';

  const getLocationDataTestId = (locationType: string) =>
    boundIndex && boundIndex >= 0
      ? `searchForm-multiBound[${boundIndex}]-${locationType}-input`
      : `searchForm-singleBound-${locationType}-input`;

  const originDataTestId = getLocationDataTestId('origin');
  const destinationDataTestId = getLocationDataTestId('destination');

  useEffect(() => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setSwitchLocationsLabel(undefined);
    }, 1000);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [switchLocationsLabel]);
  return isRevolut ? (
    <Inline className={locationWrapperStyles}>
      <LocationCombobox
        data-testid={originDataTestId}
        description={t('ScreenReader.SearchForm.From')}
        iconPrefix={<AirplaneDepartureIcon className={cx(rtlMirrored, iconStyles)} />}
        id={originDataTestId}
        label={t('Searchform.Origin.Label')}
        name={originName}
        placeholder={t('Air.Search.CitiesSelection.Placeholder.From')}
      />
      <button
        aria-label={t('Air.Search.SwitchLocations.Label')}
        className={cx(resetButtonStyle, switchDirectionStyles)}
        data-testid={getButtonDataTestId()}
        onClick={onSwitchDirection}
        title={t('Air.Search.SwitchLocations.Label')}
        type="button"
      >
        <SwitchDirection className={switchArrowsStyles} />
      </button>

      <LocationCombobox
        data-testid={destinationDataTestId}
        description={t('ScreenReader.SearchForm.To')}
        iconSuffix={<AirplaneArrivalIcon className={cx(rtlMirrored, iconStyles)} />}
        id={destinationDataTestId}
        label={t('Searchform.Destination.Label')}
        name={destinationName}
        onBlur={handleDestinationOnBlur}
        placeholder={t('Air.Search.CitiesSelection.Placeholder.To')}
      />
    </Inline>
  ) : (
    <>
      <LocationCombobox
        data-testid={originDataTestId}
        description={t('ScreenReader.SearchForm.From')}
        iconPrefix={<AirplaneTakeoff className={rtlMirrored} weight="light" />}
        id={originDataTestId}
        label={t('Searchform.Origin.Label')}
        name={originName}
        onSwitchDirection={onSwitchDirection}
        placeholder={t('Air.Search.CitiesSelection.Placeholder.From')}
      />
      {isWiderViewport && (
        <Stack spacing={4}>
          <ZeroWidthSpace />
          <SwitchDirectionButton
            aria-label={t('Air.Search.SwitchLocations.Label')}
            className={switchDirectionButtonStyles}
            data-testid={getButtonDataTestId()}
            onClick={onSwitchDirection}
            title={t('Air.Search.SwitchLocations.Label')}
            type="button"
          />
        </Stack>
      )}
      <LocationCombobox
        data-testid={destinationDataTestId}
        description={t('ScreenReader.SearchForm.To')}
        iconPrefix={<AirplaneLanding className={rtlMirrored} weight="light" />}
        id={destinationDataTestId}
        label={t('Searchform.Destination.Label')}
        name={destinationName}
        onBlur={handleDestinationOnBlur}
        placeholder={t('Air.Search.CitiesSelection.Placeholder.To')}
      />
      <span
        aria-label={switchLocationsLabel}
        aria-live="assertive"
        className={ariaLiveStyles}
        role="alert"
      />
    </>
  );
};

export default OriginAndDestination;
